import { Component, computed, inject, input, signal } from '@angular/core';
import { UiService } from '../../../services/ui.service';
import { I18nService } from '../../../services/i18n.service';
import { Router } from '@angular/router';
import { MoreInfoComponent } from '../../../components/more-info/more-info';
import { NavButtonComponent } from '../../../components/nav-button/nav-button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { SupportedLanguage } from '../../../enums/i18.enum';
import {
  DocumentTypeEnum,
  Equipment_Type,
  Plant_Structure_Enums,
} from '../../../enums/global.enum';
import { GLOBAL_CONSTANT } from '../../../constants/global.constant';
import { App_Routes } from '../../../enums/router.enum';

const availableDocuments_FL = [
  {
    docType: DocumentTypeEnum.PRODUCT,
    language: SupportedLanguage.EN,
    url: 'https://dm.henkel-dam.com/is/content/henkel/LOCTITE-Pulse-Smart-Flange-PDS',
  },
  {
    docType: DocumentTypeEnum.MANUAL,
    language: SupportedLanguage.EN,
    url: 'https://dm.henkel-dam.com/is/content/henkel/LOCTITE-Pulse-Smart-Flange-Operating-Manual',
  },
];
const availableDocuments_ST = [
  {
    docType: DocumentTypeEnum.PRODUCT,
    language: SupportedLanguage.EN,
    url: 'https://dm.henkel-dam.com/is/content/henkel/LOCTITE-Pulse-Smart-Steam-Trap-PDS-Version',
  },
  {
    docType: DocumentTypeEnum.MANUAL,
    language: SupportedLanguage.EN,
    url: 'https://dm.henkel-dam.com/is/content/henkel/LOCTITE-Pulse-Smart-Steam-Trap-Operating-Manual',
  },
];
const availableDocuments_RE = [
  {
    docType: DocumentTypeEnum.PRODUCT,
    language: SupportedLanguage.EN,
    url: 'https://dm.henkel-dam.com/is/content/henkel/LOCTITE-Pulse-Smart-Rotating-Equipment-PDS',
  },
  {
    docType: DocumentTypeEnum.MANUAL,
    language: SupportedLanguage.EN,
    url: 'https://dm.henkel-dam.com/is/content/henkel/LOCTITE-Pulse-Smart-Rotating-Equipment-Operating-Manual',
  },
];

const ALL_DOCUMENTS = [
  ...availableDocuments_FL,
  ...availableDocuments_ST,
  ...availableDocuments_RE,
];

@Component({
  selector: 'app-technical-documents-subpage',
  imports: [MoreInfoComponent, NavButtonComponent, NzCardModule],
  templateUrl: './technical-documents-subpage.html',
  styleUrl: './technical-documents-subpage.scss',
})
export class TechnicalDocumentSubpage {
  public ui = inject(UiService);
  public i18n = inject(I18nService);
  private router = inject(Router);
  page = input<string>(GLOBAL_CONSTANT.EMPTY_STRING);
  currentPage = signal<string>(GLOBAL_CONSTANT.EMPTY_STRING);

  availableDocTypes = signal<string[]>([]);

  availableLanguages = signal<any>({});
  selectedLanguages = signal<any>({});
  isMobile = computed(() => this.ui.IsMobile);
  isTablet = computed(() => this.ui.IsTablet);
  isDesktop = computed(() => this.ui.IsDesktop);

  //  Convert Getter (AvailableDocuments) to computed() signal
  AvailableDocuments = computed(() => {
    const currentPage = this.page();
    switch (currentPage) {
      case Equipment_Type.FLANGE:
        return availableDocuments_FL;
      case Equipment_Type.STEAMTRAP:
        return availableDocuments_ST;
      case 'rotatingequipment':
        return availableDocuments_RE;
      default:
        return [];
    }
  });

  constructor() {
    // 1. Calculate distinct document types
    const docTypes = [...new Set(ALL_DOCUMENTS.map((d) => d.docType))];
    this.availableDocTypes.set(docTypes);

    // 2. Calculate available languages per docType and set initial selected language
    const availableLangs: Record<string, string[]> = {};
    const selectedLangs: Record<string, string> = {};

    docTypes.forEach((docType) => {
      const languages = [
        ...new Set(ALL_DOCUMENTS.filter((d) => d.docType === docType).map((d) => d.language)),
      ];
      availableLangs[docType] = languages;
      // Preselect the first available language, or 'en' as fallback
      selectedLangs[docType] = languages[0] || SupportedLanguage.EN;
    });

    this.availableLanguages.set(availableLangs);
    this.selectedLanguages.set(selectedLangs);
  }

  findDocument(docType: string) {
    const selectedLang = this.selectedLanguages()[docType];
    return this.AvailableDocuments().find(
      (d) => d.docType === docType && d.language === selectedLang
    );
  }

  updateSelectedLanguage(docType: string, event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const newLanguage = selectElement.value;

    this.selectedLanguages.update((current) => ({
      ...current,
      [docType]: newLanguage,
    }));
  }

  openDocument(docType: string) {
    const document = this.findDocument(docType);
    if (document) {
      window.open(document.url, '_blank');
    } else {
      console.error(
        `Document not found for type ${docType} and language ${this.selectedLanguages()[docType]}`
      );
    }
  }

  toFlangeDocuments() {
    this.router.navigate([
      App_Routes.SETTINGS,
      Plant_Structure_Enums.TECHNICAL_DOCUMENTS,
      Equipment_Type.FLANGE,
    ]);
  }
  toSteamtrapDocuments() {
    this.router.navigate([
      App_Routes.SETTINGS,
      Plant_Structure_Enums.TECHNICAL_DOCUMENTS,
      Equipment_Type.STEAMTRAP,
    ]);
  }
  toRotatingequipmentDocuments() {
    this.router.navigate([
      App_Routes.SETTINGS,
      Plant_Structure_Enums.TECHNICAL_DOCUMENTS,
      'rotatingequipment',
    ]);
  }
}
